<template>
  <i-box>
    <span slot="title">
      <h3 class="pull-left">{{ title }}</h3>
      <span class="pull-right text-muted count">
        <em>
          <a :href="url" target="_blank">{{ url }}</a>
          &nbsp;
          <el-button size="mini" @click="handleShareSurvey">
            <i class="fa fa-share-alt"></i>
          </el-button>
        </em>
      </span>
      <div class="clearfix"></div>
    </span>
    <span slot="content">
      <div class="row">
        <div class="col-lg-9" v-if="!questionCharts.length">
          <div
            class="empty"
          >No entries received for this spot survey yet, charts will display once we've received more data.</div>
        </div>
        <div class="col-lg-9" v-if="questionCharts.length">
          <div class="row">
            <div
              v-for="question in questionCharts"
              :class="`col-lg-${ 6 / questionCharts.length }`"
              :key="question.question"
            >
              <h4 class="chart-title">{{ question.question }}</h4>
              <bar-chart
                :height="350"
                :showYAxes="true"
                :showXAxes="false"
                :colorCycle="generateColorPool(question.answers, hue)"
                :data="totalDisplay(question.answers)"
                :options="chartOptions"
                :autoSkipX="false"
                :maxXRotation="90"
                @onClick="handleChartClicked($event, question)"
              />
              <chart-legend
                :colorCycle="generateColorPool(question.answers, hue)"
                :data="totalDisplay(question.answers)"
                :isPercentage="totalDisplayOption === 'percentage'"
              />
            </div>

            <div class="col-lg-6">
              <div class="row" v-for="i in profileRowCount" :key="i">
                <div class="col-lg-6 text-center" v-for="key in profileKeysForRow(i)" :key="key">
                  <div class="v-chart-block">
                    <spot-profile-chart
                      :field="key"
                      :hue-range="hue"
                      :chartData="profileCharts[key]"
                      :totalDisplayOption="totalDisplayOption"
                      @onRequestRespondents="handleRequestRespondents"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-3">
          <h4>Total entries received</h4>
          <div class="entries">{{ entries.toLocaleString() }}</div>
          <hr>

          <h4>Daily entry rate</h4>
          <div class="chart">
            <trend :points="entryChart" :height="50"/>
          </div>
          <hr>

          <h4>Duration progress</h4>
          <div class="progress">
            <div :style="{width: `${progress}%`}" class="progress-bar"></div>
          </div>
          <div class="date-range">
            <p>
              Survey is
              <strong>{{ progress }}%</strong> complete.
            </p>
            <p>
              Running from
              <strong>{{ startDate | moment('MMM Do YYYY') }}</strong> to
              <strong>{{ endDate | moment('MMM Do YYYY') }}</strong>
            </p>
          </div>
        </div>
      </div>
    </span>
  </i-box>
</template>

<script>
/* eslint-disable no-unused-vars */
import moment from 'moment';
/* eslint-enable no-unused-vars */

import colorMixin from '@/mixins/colors';

import IBox from '@/components/common/IBox';
import Trend from '@/components/charts/Trend';

import BarChart from '@/components/charts/BarChart';
import HorizontalBarChart from '@/components/charts/HorizontalBarChart';
import PieChart from '@/components/charts/PieChart';
import DonutChart from '@/components/charts/DoughnutChart';
import ChartLegend from '@/components/charts/ChartLegend';

import SpotProfileChart from '@/components/surveys/SpotProfileChart';

export default {
  name: 'SpotItem',
  components: {
    IBox,
    Trend,

    BarChart,
    HorizontalBarChart,
    PieChart,
    DonutChart,
    ChartLegend,

    SpotProfileChart,
  },
  mixins: [colorMixin],
  props: {
    id: {
      type: Number,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    url: {
      type: String,
      required: true,
    },
    startDate: {
      type: Date,
      required: true,
    },
    endDate: {
      type: Date,
      required: true,
    },
    entries: {
      type: Number,
      default: 0,
    },
    entryChart: {
      type: Array,
      required: true,
    },
    questionCharts: {
      type: Array,
      required: true,
    },
    profileCharts: {
      type: Object,
      required: true,
    },
    totalDisplayOption: {
      type: String,
      default: 'percentage',
    },
  },
  data: () => ({
    profileItemsPerRow: 2,
  }),
  computed: {
    progress() {
      const totalSeconds = this.endDate.getTime() - this.startDate.getTime();
      const elapsedSeconds = new Date().getTime() - this.startDate.getTime();
      let percentage = elapsedSeconds / totalSeconds * 100;

      if (percentage > 100) {
        percentage = 100;
      }

      if (percentage < 0) {
        percentage = 0;
      }

      return Math.round(percentage);
    },
    hue() {
      const seed = this.generateSeedFromString(this.title, 12);
      const hueStart = this.generateRnd(seed, 1, 340);
      const hueEnd = hueStart + 20;
      const hueRange = [hueStart, hueEnd];
      return hueRange;
    },

    totalDisplay() {
      return answers => answers.map(a => [a.title, a[this.totalDisplayOption]]);
    },
    chartOptions() {
      return {
        tooltips: {
          callbacks: {
            label: this.generateTooltipLabel,
          },
        },
      };
    },
    pieChartOptions() {
      return {
        tooltips: {
          callbacks: {
            label: this.generatePieTooltipLabel,
          },
        },
      };
    },
    profileRowCount() {
      return Math.ceil(Object.keys(this.profileCharts).length / this.profileItemsPerRow);
    },
    profileKeysForRow() {
      return row =>
        Object.keys(this.profileCharts).slice(
          (row - 1) * this.profileRowCount,
          row * this.profileRowCount,
        );
    },
  },
  methods: {
    generateTooltipLabel(tooltipItem, data) {
      const label = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] || '';

      const formatMap = {
        percentage: l => `${l}%`,
        total: l => l.toLocaleString(),
        cume: l => l.toLocaleString(),
      };

      return formatMap[this.totalDisplayOption](label);
    },

    generatePieTooltipLabel(tooltipItem, data) {
      const total = this.generateTooltipLabel(tooltipItem, data);
      const label = data.labels[tooltipItem.index];

      return `${label}: ${total}`;
    },

    handleChartClicked(event, questionItem) {
      /* eslint-disable no-underscore-dangle */
      const answer = questionItem.answers[event[1][0]._index];
      this.respondentFiltering = {
        choice_id: answer.choice_id,
        question_id: questionItem.question_id,
      };
      this.handleRequestRespondents({
        filtering: this.respondentFiltering,
        title: questionItem.question,
        subtitle: answer.title,
      });
    },

    handleRequestRespondents(request) {
      request.filtering = {
        ...request.filtering,
        filter_by: 'survey',
        survey_id: this.id,
      };
      this.$emit('onRequestRespondents', request);
    },

    handleShareSurvey() {
      this.$store.dispatch('modals/showSocialPostDialog', {
        message: `Please complete our short survey ${this.url}`,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.entries {
  font-size: 36px;
}
.chart-title {
  height: 2.2em;
}
.v-chart-block {
  &.bottom {
    margin-top: 20px;
  }
}
.empty {
  font-size: 1.4em;
  color: #ccc;
}
</style>



// WEBPACK FOOTER //
// SpotItem.vue?92f21818